import { useCallback, useEffect, useRef, useState } from 'react';

import { TASK_COMPLETION_DELAY } from '../model/consts';

import type { TDeal } from '@crm/entities/deal';

export const useDelayedTaskCompletion = (dealId: TDeal['id']) => {
    const [pendingTaskIds, setPendingTaskIds] = useState<Set<string>>(() => new Set());
    const timeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    useEffect(() => {
        setPendingTaskIds(new Set());

        return () => {
            Object.values(timeoutsRef.current).forEach(clearTimeout);
            timeoutsRef.current = {};
        };
    }, [dealId]);

    const cancelTaskCompletion = useCallback((taskUid: string) => {
        const timeout = timeoutsRef.current[taskUid];

        if (!timeout) {
            return false;
        }

        clearTimeout(timeout);
        delete timeoutsRef.current[taskUid];
        setPendingTaskIds((currentIds) => {
            const nextIds = new Set(currentIds);
            nextIds.delete(taskUid);

            return nextIds;
        });

        return true;
    }, []);

    const scheduleTaskCompletion = useCallback((taskUid: string, onComplete: () => void) => {
        if (timeoutsRef.current[taskUid]) {
            return;
        }

        setPendingTaskIds((currentIds) => new Set(currentIds).add(taskUid));
        timeoutsRef.current[taskUid] = setTimeout(() => {
            delete timeoutsRef.current[taskUid];
            setPendingTaskIds((currentIds) => {
                const nextIds = new Set(currentIds);
                nextIds.delete(taskUid);

                return nextIds;
            });
            onComplete();
        }, TASK_COMPLETION_DELAY);
    }, []);

    return { pendingTaskIds, cancelTaskCompletion, scheduleTaskCompletion };
};
